import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

// Per-card image slider — cycles cover + gallery every 3.5s with crossfade. Pauses on hover.
const CardSlider = ({ images, alt, testid, hovered }) => {
    const [i, setI] = useState(0);
    useEffect(() => {
        if (images.length <= 1 || hovered) return;
        const t = setInterval(() => setI((v) => (v + 1) % images.length), 3500);
        return () => clearInterval(t);
    }, [images.length, hovered]);
    return (
        <>
            {images.map((src, idx) => (
                <img
                    key={idx}
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out"
                    style={{ opacity: idx === i ? 1 : 0 }}
                    data-testid={`${testid}-slide-${idx}`}
                />
            ))}
            {images.length > 1 && (
                <div className="absolute bottom-3 left-3 z-10 flex gap-1.5">
                    {images.map((_, idx) => (
                        <span
                            key={idx}
                            className={`block h-[2px] transition-all duration-500 ${
                                idx === i ? "w-6 bg-[var(--st-cream)]" : "w-3 bg-[var(--st-cream)]/40"
                            }`}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

const Work = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("All");
    const [hoveredId, setHoveredId] = useState(null);

    useEffect(() => {
        api.get("/case-studies").then((r) => setItems(r.data)).catch(() => {});
    }, []);

    const industries = ["All", ...Array.from(new Set(items.map((i) => i.industry)))];
    const filtered = filter === "All" ? items : items.filter((i) => i.industry === filter);

    return (
        <main data-testid="work-page" className="bg-[var(--st-cream)]">
            {/* Page header */}
            <section className="pt-36 lg:pt-44 pb-16 lg:pb-20">
                <div className="st-container">
                    <p className="eyebrow text-[var(--st-gold-deep)] mb-6">Selected work · 2010 — {new Date().getFullYear()}</p>
                    <h1 className="font-serif leading-[0.92] tracking-tighter" style={{ fontSize: "clamp(3rem, 8vw, 7.5rem)" }}>
                        Brands we've<br />
                        <span className="italic text-[var(--st-gold)]">quietly shaped.</span>
                    </h1>
                    <p className="mt-8 text-lg leading-relaxed text-[var(--st-ink-soft)] max-w-2xl">
                        A selection of {items.length} recent projects — across wellness, hospitality, luxury interiors,
                        food, fashion, film and events. Each one built around a specific problem, a specific founder,
                        and a specific promise.
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="border-t border-b border-[var(--st-line)] py-5 sticky top-[72px] bg-[var(--st-cream)]/95 backdrop-blur z-30">
                <div className="st-container flex gap-2.5 overflow-x-auto no-scrollbar items-center">
                    <span className="eyebrow text-[var(--st-ink-soft)] shrink-0 mr-3 hidden md:inline">Filter by industry —</span>
                    {industries.map((ind) => (
                        <button
                            key={ind}
                            onClick={() => setFilter(ind)}
                            data-testid={`work-filter-${ind.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className={`shrink-0 px-4 py-2 text-[11px] uppercase tracking-widest border transition-colors ${
                                filter === ind
                                    ? "bg-[var(--st-ink)] text-[var(--st-cream)] border-[var(--st-ink)]"
                                    : "border-[var(--st-line)] hover:border-[var(--st-ink)] text-[var(--st-ink-soft)]"
                            }`}
                        >
                            {ind}
                        </button>
                    ))}
                </div>
            </section>

            {/* Portfolio grid — uniform 3-column, magazine style */}
            <section className="py-16 lg:py-24">
                <div className="st-container">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20 lg:gap-y-24">
                        {filtered.map((cs, i) => (
                            <motion.article
                                key={cs.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
                                data-testid={`work-card-${cs.slug}`}
                                onMouseEnter={() => setHoveredId(cs.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                className="group"
                            >
                                <Link to={`/work/${cs.slug}`} className="block">
                                    <div className="relative aspect-[4/5] overflow-hidden bg-[var(--st-ink)] mb-6 border border-[rgba(180,153,90,0.25)]">
                                        {/* Elegant gold-on-ink nameplate — no image */}
                                        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                                            {/* Ornamental frame */}
                                            <div className="absolute inset-6 border border-[rgba(180,153,90,0.35)]" />
                                            <div className="absolute inset-6 border-t-2 border-b-2 border-[var(--st-gold)]/0 group-hover:border-[var(--st-gold)]/60 transition-all duration-700" />

                                            {/* Case number */}
                                            <p className="eyebrow text-[var(--st-gold)] mb-6 z-10">
                                                Case · {String(i + 1).padStart(2, '0')}
                                            </p>

                                            {/* Gold decorative divider */}
                                            <div className="flex items-center gap-3 mb-8 z-10">
                                                <span className="block h-px w-8 bg-[var(--st-gold)]/60" />
                                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--st-gold)]" />
                                                <span className="block h-px w-8 bg-[var(--st-gold)]/60" />
                                            </div>

                                            {/* Client name — the star */}
                                            <h3 className="font-serif text-4xl md:text-[2.4rem] leading-[1.05] tracking-tight text-[var(--st-gold)] z-10 max-w-[85%]">
                                                {cs.client}
                                            </h3>

                                            {/* Italic tagline hint */}
                                            <p className="font-serif italic text-sm text-[#c8c5b3] mt-6 z-10 max-w-[80%] line-clamp-2">
                                                {cs.tagline}
                                            </p>

                                            {/* View link at bottom */}
                                            <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-2 eyebrow text-[var(--st-cream)] opacity-70 group-hover:opacity-100 group-hover:text-[var(--st-gold)] transition-all duration-500 z-10">
                                                View case study <ArrowUpRight size={13} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Caption block — same structure for every card */}
                                    <div className="border-t border-[var(--st-line)] pt-5">
                                        <p className="eyebrow text-[var(--st-gold-deep)] mb-3">
                                            {cs.industry} · {cs.year}
                                        </p>
                                        <h3 className="font-serif text-3xl md:text-[2rem] leading-tight tracking-tight group-hover:text-[var(--st-gold-deep)] transition-colors duration-500">
                                            {cs.client}
                                        </h3>
                                        <p className="font-serif italic text-base text-[var(--st-ink-soft)] mt-2 line-clamp-2">
                                            {cs.tagline}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {(cs.services || []).slice(0, 3).map((s) => (
                                                <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[var(--st-line)] text-[var(--st-ink-soft)]">
                                                    {s}
                                                </span>
                                            ))}
                                            {(cs.services || []).length > 3 && (
                                                <span className="text-[10px] uppercase tracking-widest px-2 py-1 text-[var(--st-gold-deep)]">
                                                    +{cs.services.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <p className="text-center text-[var(--st-ink-soft)] py-20">No projects in this category yet.</p>
                    )}
                </div>
            </section>
        </main>
    );
};

export default Work;
